// The survey pass: the hatch of unsurveyed water, ONE full-world quad
// (v2-the-deep.md §4.1, chart card 20 as round 088 card 06 draws it,
// ui-map-interface.md §4.5, R2626).
//
// The survey plane (`uSurvey`, R8UI, one texel a cell) holds 1 on a cell
// the survey of the viewer's faction holds. A cell with 0 takes a veil in
// the deep ink and diagonal lines in the text ink. The lines are a fract
// on the world point, so they stay on the water while the camera pans,
// and they fade out while two lines stand closer than `uSvFadePx` CSS px
// on the screen: a far zoom shows the veil alone and no grey wash.
//
// The pass draws no geometry of its own: the deltas write texels, and the
// quad is the same each frame (§10).

import { SURVEY_HATCH } from '../../gen/presentation';
import { PRELUDE, SHARED_UNIFORMS, type UniformSpec } from './prelude';
import { rgb } from '../../render/color';
import { worldProgram } from '../gfx/worldquad';
import type { ProgramSource } from './program';
import { surveyBodyWgsl } from './wgsl/survey';

/** The survey pass's own uniforms, on top of `SHARED_UNIFORMS`. */
export const SURVEY_UNIFORMS: readonly UniformSpec[] = [
  { name: 'uSvVeil', type: 'vec3', value: rgb(SURVEY_HATCH.veilInk), note: 'the deep ink of the palette' },
  { name: 'uSvVeilAlpha', type: 'float', value: SURVEY_HATCH.veilAlpha },
  { name: 'uSvInk', type: 'vec3', value: rgb(SURVEY_HATCH.lineInk), note: 'the text ink of the palette' },
  { name: 'uSvLineAlpha', type: 'float', value: SURVEY_HATCH.lineAlpha, dynamic: true, note: 'the alpha of the reading' },
  { name: 'uSvSpacing', type: 'float', value: SURVEY_HATCH.spacingPx, note: 'world px between two lines' },
  { name: 'uSvLinePx', type: 'float', value: SURVEY_HATCH.linePx, note: 'the line width in CSS px' },
  { name: 'uSvFadePx', type: 'float', value: SURVEY_HATCH.fadePx, note: 'the CSS px spacing the lines fade under' },
];

/** Every uniform the survey program declares. */
export const SURVEY_ALL_UNIFORMS: readonly UniformSpec[] = [...SHARED_UNIFORMS, ...SURVEY_UNIFORMS];

const BODY = `
uniform vec3  uSvVeil;
uniform float uSvVeilAlpha;
uniform vec3  uSvInk;
uniform float uSvLineAlpha;
uniform float uSvSpacing;
uniform float uSvLinePx;
uniform float uSvFadePx;

// A cell outside the grid is no water of the survey, so it draws nothing.
float surveyedOf(ivec2 c) {
  return inGrid(c) ? float(texelFetch(uSurvey, c, 0).r) : 1.0;
}

void main() {
  vec2 p = vWorld;
  ivec2 c = cellAt(p);
  if (surveyedOf(c) > 0.5) { fragColor = vec4(0.0); return; }

  float px = worldPx();
  // The lines run at 45 degrees: the distance along their normal.
  float t = (p.x + p.y) * 0.7071067811865476;
  float d = abs(fract(t / uSvSpacing + 0.5) - 0.5) * uSvSpacing;
  float cov = lineCov(d, 0.5 * uSvLinePx * uDpr * px, px);
  float apart = uSvSpacing * uZoom;
  float fade = uSvFadePx > 0.0 ? clamp((apart - uSvFadePx) / uSvFadePx, 0.0, 1.0) : 1.0;

  vec4 o = vec4(uSvVeil * uSvVeilAlpha, uSvVeilAlpha);
  fragColor = over(o, uSvInk, uSvLineAlpha * cov * fade);
}
`;

/** The complete survey fragment shader, prelude included. */
export const SURVEY_FRAG = PRELUDE + BODY;

/** The survey pass, as both backends read it. */
export function surveyProgram(): ProgramSource {
  return worldProgram({
    name: 'survey', fragment: SURVEY_FRAG, raw: true, uniforms: SURVEY_ALL_UNIFORMS,
    wgslBody: surveyBodyWgsl(),
  });
}
